import { apiUrl } from "../api/endpoints";
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MicIcon from "@mui/icons-material/Mic";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import NavigationIcon from "@mui/icons-material/Navigation";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import Spinner from "../Components/Spinner";
import Widget from "../widgets/Widget";

import { useAuth } from "../hooks/AuthProvider";
import { LanguageContext } from "../context/LanguageContext";
import { AI_REPORT_PARAM_INPUT_TYPES, AI_REPORT_PDF_MODES, normalizeAiReportParamInputType, normalizeAiReportParamOptions, normalizeAiReportPdfMode, setAiReportTypesCache } from "../utils/aiReportTypes";
import { ASSET_CARD_DEVICE_DETAILS_FIELDS, DEFAULT_ASSET_CARD_CONFIG, normalizeAssetCardConfig, normalizeStringList } from "../utils/assetCardConfig";
import { fetchVoiceMediaLibrary, normalizeVoiceCommandConfig } from "../services/aiVoiceAssistantApi";
import {
  DASHBOARD_WIDGET_FONT_SIZE_FIELDS,
  normalizeDashboardWidgetFontSizes,
  notifyDashboardWidgetFontSizesUpdated,
} from "../utils/dashboardWidgetFontSizes";
import { getWebDashboardWidgetDefaults } from "./WebDashboard/layout/webDashboardWidgetDefaults";
import systemConfigTranslations from "../languages/systemConfigurations.json";
import "./SystemConfigurations.css";

const createBlankReportType = () => ({ value: "", label: "", apiUrl: "", pdfMode: "ai_pdf", params: [] });
const createBlankParam = () => ({ key: "", label: "", required: true, placeholder: "", inputType: "text", options: [], defaultValue: "", system: false });
const createBlankAssetCategory = () => ({ value: "", label: "", assetTypes: [] });
const createBlankVoiceAction = () => ({
  id: `voice-${Date.now()}`,
  label: "New media command",
  phrases: ["sama"],
  action: { type: "media", media: { path: "", title: "", mediaType: "image" } },
});
const normalizeAiReportAvailability = (value = {}) => ({
  devices: value.devices !== false,
  dashboards: value.dashboards !== false,
  templates: value.templates !== false,
});

const normalizeNewSubscriberPermissionExclusions = (value = {}) => ({
  excludedPermissionIds: Array.from(
    new Set(
      (Array.isArray(value?.excludedPermissionIds) ? value.excludedPermissionIds : [])
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b)),
});

const normalizeReportTypes = (value) =>
  (Array.isArray(value?.types) ? value.types : []).map((type) => ({
    value: String(type?.value || ""),
    label: String(type?.label || ""),
    apiUrl: String(type?.apiUrl || ""),
    pdfMode: normalizeAiReportPdfMode(type?.pdfMode || type?.outputMode || type?.renderer, type?.apiUrl),
    params: (Array.isArray(type?.params) ? type.params : []).map((param) => ({
      key: String(param?.key || ""),
      label: String(param?.label || ""),
      required: param?.required !== false,
      placeholder: String(param?.placeholder || ""),
      inputType: normalizeAiReportParamInputType(param?.inputType ?? param?.type),
      options: normalizeAiReportParamOptions(param?.options ?? param?.values),
      defaultValue: param?.defaultValue === undefined || param?.defaultValue === null ? "" : String(param.defaultValue),
      system: param?.system === true || param?.hidden === true,
    })),
  }));

const serializeOptions = (options = []) =>
  normalizeAiReportParamOptions(options)
    .map((option) => option.value)
    .join(", ");

const normalizeMode = (mode) =>
  mode === "anovator_redirect" ? "anovator_redirect" : "internal_pdf";

const DEFAULT_WEATHER_CRON_CONFIG = {
  enabled: true,
  intervalMinutes: 30,
  timezone: "Asia/Dubai",
  requestTimeoutMs: 10000,
  providers: [
    { id: "open_meteo_air_quality", enabled: true, role: "air_quality" },
    { id: "open_meteo_forecast", enabled: true, role: "weather" },
    { id: "met_no_forecast", enabled: true, role: "weather" },
    { id: "openweathermap_air_pollution", enabled: false, role: "air_quality", apiKeyEnv: "OPENWEATHER_API_KEY" },
  ],
  thresholds: {
    lightDustUgM3: 5,
    moderateDustUgM3: 20,
    heavyDustUgM3: 50,
    sandstormDustUgM3: 150,
    dustyPm10UgM3: 100,
    hazyAerosolOpticalDepth: 0.6,
  },
};

const WEATHER_PROVIDER_LABELS = {
  open_meteo_air_quality: "Open-Meteo Air Quality",
  open_meteo_forecast: "Open-Meteo Forecast",
  met_no_forecast: "MET Norway Forecast",
  openweathermap_air_pollution: "OpenWeather Air Pollution",
};

const WEATHER_THRESHOLD_FIELDS = [
  { key: "lightDustUgM3", label: "Light dust", unit: "ug/m3" },
  { key: "moderateDustUgM3", label: "Moderate dust", unit: "ug/m3" },
  { key: "heavyDustUgM3", label: "Heavy dust", unit: "ug/m3" },
  { key: "sandstormDustUgM3", label: "Sandstorm", unit: "ug/m3" },
  { key: "dustyPm10UgM3", label: "Dusty PM10", unit: "ug/m3" },
  { key: "hazyAerosolOpticalDepth", label: "Hazy AOD", unit: "AOD" },
];

const normalizeNumber = (value, fallback, { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
};

const normalizeWeatherCronConfig = (value = {}) => {
  const providerMap = new Map(DEFAULT_WEATHER_CRON_CONFIG.providers.map((provider) => [provider.id, { ...provider }]));
  (Array.isArray(value.providers) ? value.providers : []).forEach((provider) => {
    const id = String(provider?.id || "").trim();
    if (!id) return;
    providerMap.set(id, {
      ...(providerMap.get(id) || {}),
      id,
      enabled: provider.enabled !== false,
      role: provider.role === "weather" ? "weather" : "air_quality",
      apiKeyEnv: typeof provider.apiKeyEnv === "string" ? provider.apiKeyEnv.trim() : providerMap.get(id)?.apiKeyEnv,
      apiKey: typeof provider.apiKey === "string" ? provider.apiKey.trim() : undefined,
    });
  });

  const thresholds = { ...DEFAULT_WEATHER_CRON_CONFIG.thresholds, ...(value.thresholds || {}) };

  return {
    ...DEFAULT_WEATHER_CRON_CONFIG,
    ...value,
    enabled: value.enabled !== false,
    intervalMinutes: normalizeNumber(value.intervalMinutes, DEFAULT_WEATHER_CRON_CONFIG.intervalMinutes, { min: 15 }),
    requestTimeoutMs: normalizeNumber(value.requestTimeoutMs, DEFAULT_WEATHER_CRON_CONFIG.requestTimeoutMs, { min: 1000 }),
    timezone: String(value.timezone || DEFAULT_WEATHER_CRON_CONFIG.timezone),
    providers: Array.from(providerMap.values()),
    thresholds: WEATHER_THRESHOLD_FIELDS.reduce((acc, field) => {
      acc[field.key] = normalizeNumber(thresholds[field.key], DEFAULT_WEATHER_CRON_CONFIG.thresholds[field.key], { min: 0 });
      return acc;
    }, {}),
  };
};

const modeIcons = {
  internal_pdf: <PictureAsPdfIcon />,
  anovator_redirect: <OpenInNewIcon />,
};

const WIDGET_PREVIEW_DEFAULT_PROP_KEYS = {
  chart: "defaultChartProps",
  "period-column": "previewPeriodColumnProps",
  gauge: "defaultGaugeProps",
  "radial-gauge": "defaultRadialGaugeProps",
  label: "defaultLabelProps",
  led: "defaultLEDProps",
  number: "defaultNumberProps",
  slider: "defaultSliderProps",
  switch: "defaultSwitchProps",
  "push-button": "defaultPushButtonProps",
  terminal: "defaultTerminalProps",
  text: "defaultTextProps",
  menu: "defaultMenuProps",
  "segment-switch": "defaultSegmentSwitchProps",
  "image-button": "defaultImageButtonProps",
  keypad: "defaultKeypadProps",
  table: "defaultTableProps",
  fan: "defaultFanProps",
  pipe: "defaultPipeProps",
  elbow: "defaultElbowProps",
  "water-pump": "defaultWaterPumpProps",
  liquid: "defaultLiquidProps",
  "water-tank": "defaultWaterTankProps",
  "text-title": "defaultTextTitleProps",
  "camera-stream": "defaultCameraStreamProps",
  "time-range": "defaultTimeRangeProps",
  "environmental-status": "defaultEnvironmentalStatusProps",
  "asset-status": "defaultAssetStatusProps",
  "status-arrow": "defaultStatusArrowProps",
};

const WIDGET_PREVIEW_TITLES = {
  chart: "Chart",
  "period-column": "Period Column",
  gauge: "Gauge",
  "radial-gauge": "Radial Gauge",
  label: "Label",
  led: "LED",
  number: "Number",
  slider: "Slider",
  switch: "Switch",
  "push-button": "Push Button",
  terminal: "Terminal",
  text: "Text",
  menu: "Menu",
  "segment-switch": "Segment Switch",
  "image-button": "Image Button",
  keypad: "Keypad",
  table: "Table",
  fan: "Fan",
  pipe: "Pipe",
  elbow: "Elbow",
  "water-pump": "Water Pump",
  liquid: "Liquid",
  "water-tank": "Water Tank",
  "text-title": "Title Widget",
  "defaultCameraStreamProps": "Camera Stream",
  "time-range": "Digital Clock",
  "environmental-status": "Environmental Status",
  "asset-status": "Asset Status",
  "status-arrow": "Status Arrow",
};

const WIDGET_PREVIEW_SIZES = {
  chart: { width: 360, height: 240 },
  "period-column": { width: 360, height: 240 },
  gauge: { width: 260, height: 240 },
  "radial-gauge": { width: 260, height: 240 },
  liquid: { width: 240, height: 240 },
  "water-tank": { width: 240, height: 240 },
  label: { width: 320, height: 150 },
  led: { width: 220, height: 150 },
  number: { width: 280, height: 180 },
  slider: { width: 320, height: 150 },
  switch: { width: 280, height: 150 },
  "push-button": { width: 280, height: 150 },
  terminal: { width: 340, height: 190 },
  text: { width: 300, height: 150 },
  "text-title": { width: 320, height: 120 },
  menu: { width: 280, height: 150 },
  "segment-switch": { width: 320, height: 150 },
  "image-button": { width: 240, height: 160 },
  keypad: { width: 260, height: 260 },
  table: { width: 360, height: 240 },
  fan: { width: 240, height: 220 },
  pipe: { width: 340, height: 170 },
  elbow: { width: 260, height: 220 },
  "water-pump": { width: 320, height: 180 },
  "time-range": { width: 360, height: 190 },
  "environmental-status": { width: 360, height: 260 },
  "asset-status": { width: 360, height: 230 },
  "status-arrow": { width: 320, height: 150 },
};

const WIDGET_PREVIEW_DATASTREAMS = [
  { id: 9001, pin: "V1", name: "Temperature", value: 32, defaultValue: 32, min: 0, max: 100, unit: "C", decimals: 0 },
  { id: 9002, pin: "V2", name: "Humidity", value: 64, defaultValue: 64, min: 0, max: 100, unit: "%", decimals: 0 },
  { id: 9003, pin: "V3", name: "Air Quality", value: 42, defaultValue: 42, min: 0, max: 100, unit: "ppm", decimals: 0 },
];

const SystemConfigurations = () => {
  const { user } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = systemConfigTranslations[lang] || systemConfigTranslations.en;
  const isRtl = ["ar", "ur"].includes(lang);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("internal_pdf");
  const [savedMode, setSavedMode] = useState("internal_pdf");
  const [reportTypes, setReportTypes] = useState([]);
  const [savedReportTypes, setSavedReportTypes] = useState([]);
  const [aiReportAvailability, setAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [savedAiReportAvailability, setSavedAiReportAvailability] = useState(normalizeAiReportAvailability());
  const [selectedTypeIndex, setSelectedTypeIndex] = useState(0);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("reports");
  const [assetCardConfig, setAssetCardConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [savedAssetCardConfig, setSavedAssetCardConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [newSubscriberPermissionExclusions, setNewSubscriberPermissionExclusions] = useState(normalizeNewSubscriberPermissionExclusions());
  const [savedNewSubscriberPermissionExclusions, setSavedNewSubscriberPermissionExclusions] = useState(normalizeNewSubscriberPermissionExclusions());
  const [permissions, setPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [aiVoiceCommands, setAiVoiceCommands] = useState(normalizeVoiceCommandConfig());
  const [savedAiVoiceCommands, setSavedAiVoiceCommands] = useState(normalizeVoiceCommandConfig());
  const [weatherCron, setWeatherCron] = useState(normalizeWeatherCronConfig());
  const [savedWeatherCron, setSavedWeatherCron] = useState(normalizeWeatherCronConfig());
  const [dashboardWidgetFontSizes, setDashboardWidgetFontSizes] = useState(normalizeDashboardWidgetFontSizes());
  const [savedDashboardWidgetFontSizes, setSavedDashboardWidgetFontSizes] = useState(normalizeDashboardWidgetFontSizes());
  const [selectedDashboardWidgetType, setSelectedDashboardWidgetType] = useState(DASHBOARD_WIDGET_FONT_SIZE_FIELDS[0]?.type || "chart");
  const [voiceMediaLibrary, setVoiceMediaLibrary] = useState([]);

  const isSuper = String(user?.role || "").toLowerCase() === "super";
  const isDirty =
    mode !== savedMode ||
    JSON.stringify(reportTypes) !== JSON.stringify(savedReportTypes) ||
    JSON.stringify(aiReportAvailability) !== JSON.stringify(savedAiReportAvailability) ||
    JSON.stringify(assetCardConfig) !== JSON.stringify(savedAssetCardConfig) ||
    JSON.stringify(newSubscriberPermissionExclusions) !== JSON.stringify(savedNewSubscriberPermissionExclusions) ||
    JSON.stringify(aiVoiceCommands) !== JSON.stringify(savedAiVoiceCommands) ||
    JSON.stringify(weatherCron) !== JSON.stringify(savedWeatherCron) ||
    JSON.stringify(dashboardWidgetFontSizes) !== JSON.stringify(savedDashboardWidgetFontSizes);

  const downloadModes = useMemo(
    () => ({
      internal_pdf: { ...t.healthModes.internal_pdf, icon: modeIcons.internal_pdf },
      anovator_redirect: { ...t.healthModes.anovator_redirect, icon: modeIcons.anovator_redirect },
    }),
    [t.healthModes]
  );
  const dashboardWidgetDefaults = useMemo(() => getWebDashboardWidgetDefaults({}, {}), []);
  const selectedDashboardWidgetField =
    DASHBOARD_WIDGET_FONT_SIZE_FIELDS.find((field) => field.type === selectedDashboardWidgetType) ||
    DASHBOARD_WIDGET_FONT_SIZE_FIELDS[0];
  const selectedDashboardWidgetSizes = (selectedDashboardWidgetField?.fields || []).reduce((acc, field) => {
    const rawValue = dashboardWidgetFontSizes.widgets?.[selectedDashboardWidgetField.type]?.[field.key];
    acc[field.key] = rawValue === "" || rawValue === null || rawValue === undefined ? field.value : rawValue;
    return acc;
  }, {});
  const selectedDashboardWidgetProps = {
    ...(dashboardWidgetDefaults[WIDGET_PREVIEW_DEFAULT_PROP_KEYS[selectedDashboardWidgetField?.type]] || {}),
    ...selectedDashboardWidgetSizes,
  };
  const selectedDashboardWidgetSize =
    WIDGET_PREVIEW_SIZES[selectedDashboardWidgetField?.type] || { width: 300, height: 180 };
  const selectedDashboardWidgetTitle =
    WIDGET_PREVIEW_TITLES[selectedDashboardWidgetField?.type] ||
    selectedDashboardWidgetField?.label ||
    "Widget";

  useEffect(() => {
    const fetchConfig = async () => {
      if (!isSuper) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(apiUrl(`configuration/system`));
        const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
        const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
        const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
        const nextAssetCardConfig = normalizeAssetCardConfig(res.data?.data?.assetCard?.value);
        const nextNewSubscriberPermissionExclusions = normalizeNewSubscriberPermissionExclusions(res.data?.data?.newSubscriberPermissionExclusions?.value);
        const nextAiVoiceCommands = normalizeVoiceCommandConfig(res.data?.data?.aiVoiceCommands?.value);
        const nextWeatherCron = normalizeWeatherCronConfig(res.data?.data?.weatherCron?.value);
        const nextDashboardWidgetFontSizes = normalizeDashboardWidgetFontSizes(res.data?.data?.dashboardWidgetFontSizes?.value);
        const [permissionsRes, mediaItems] = await Promise.all([
          axios.get(apiUrl(`permission/list`)),
          fetchVoiceMediaLibrary().catch(() => []),
        ]);
        setPermissions(Array.isArray(permissionsRes.data?.data) ? permissionsRes.data.data : []);
        setVoiceMediaLibrary(mediaItems);
        setMode(nextMode);
        setSavedMode(nextMode);
        setReportTypes(nextReportTypes);
        setSavedReportTypes(nextReportTypes);
        setAiReportAvailability(nextAvailability);
        setSavedAiReportAvailability(nextAvailability);
        setAssetCardConfig(nextAssetCardConfig);
        setSavedAssetCardConfig(nextAssetCardConfig);
        setNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
        setSavedNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
        setAiVoiceCommands(nextAiVoiceCommands);
        setSavedAiVoiceCommands(nextAiVoiceCommands);
        setWeatherCron(nextWeatherCron);
        setSavedWeatherCron(nextWeatherCron);
        setDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
        setSavedDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
        setSelectedTypeIndex(0);
      } catch (err) {
        console.error(err);
        setMessage(t.loadError);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [isSuper, t.loadError]);

  const currentModeLabel = downloadModes[savedMode]?.title || t.healthModes.internal_pdf.title;
  const selectedType = reportTypes[selectedTypeIndex] || null;
  const updateAiReportAvailability = (key, checked) => {
    setAiReportAvailability((prev) => ({ ...prev, [key]: checked }));
  };

  const save = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await axios.put(apiUrl(`configuration/system`), {
        healthReport: { downloadMode: mode },
        aiReportTypes: { types: reportTypes },
        aiReportAvailability,
        assetCard: assetCardConfig,
        newSubscriberPermissionExclusions,
        aiVoiceCommands,
        weatherCron,
        dashboardWidgetFontSizes,
      });
      const nextMode = normalizeMode(res.data?.data?.healthReport?.value?.downloadMode);
      const nextReportTypes = normalizeReportTypes(res.data?.data?.aiReportTypes?.value);
      const nextAvailability = normalizeAiReportAvailability(res.data?.data?.aiReportAvailability?.value);
      const nextAssetCardConfig = normalizeAssetCardConfig(res.data?.data?.assetCard?.value);
      const nextNewSubscriberPermissionExclusions = normalizeNewSubscriberPermissionExclusions(res.data?.data?.newSubscriberPermissionExclusions?.value);
      const nextAiVoiceCommands = normalizeVoiceCommandConfig(res.data?.data?.aiVoiceCommands?.value);
      const nextWeatherCron = normalizeWeatherCronConfig(res.data?.data?.weatherCron?.value);
      const nextDashboardWidgetFontSizes = normalizeDashboardWidgetFontSizes(res.data?.data?.dashboardWidgetFontSizes?.value);
      setMode(nextMode);
      setSavedMode(nextMode);
      setReportTypes(nextReportTypes);
      setSavedReportTypes(nextReportTypes);
      setAiReportAvailability(nextAvailability);
      setSavedAiReportAvailability(nextAvailability);
      setAssetCardConfig(nextAssetCardConfig);
      setSavedAssetCardConfig(nextAssetCardConfig);
      setNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
      setSavedNewSubscriberPermissionExclusions(nextNewSubscriberPermissionExclusions);
      setAiVoiceCommands(nextAiVoiceCommands);
      setSavedAiVoiceCommands(nextAiVoiceCommands);
      setWeatherCron(nextWeatherCron);
      setSavedWeatherCron(nextWeatherCron);
      setDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
      setSavedDashboardWidgetFontSizes(nextDashboardWidgetFontSizes);
      notifyDashboardWidgetFontSizesUpdated(nextDashboardWidgetFontSizes);
      setAiReportTypesCache(nextReportTypes, nextAvailability);
      setSelectedTypeIndex(Math.min(selectedTypeIndex, Math.max(0, nextReportTypes.length - 1)));
      setMessage(t.saveSuccess);
    } catch (err) {
      console.error(err);
      setMessage(t.saveError);
    } finally {
      setSaving(false);
    }
  };

  const updateType = (index, patch) => {
    setReportTypes((prev) => prev.map((type, idx) => (idx === index ? { ...type, ...patch } : type)));
  };

  const addType = () => {
    setReportTypes((prev) => {
      const next = [...prev, createBlankReportType()];
      setSelectedTypeIndex(next.length - 1);
      return next;
    });
  };

  const deleteType = (index) => {
    setReportTypes((prev) => prev.filter((_, idx) => idx !== index));
    setSelectedTypeIndex((prev) => Math.max(0, prev - 1));
  };

  const updateParam = (typeIndex, paramIndex, patch) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? {
              ...type,
              params: type.params.map((param, pIdx) =>
                pIdx === paramIndex ? { ...param, ...patch } : param
              ),
            }
          : type
      )
    );
  };

  const addParam = (typeIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex ? { ...type, params: [...type.params, createBlankParam()] } : type
      )
    );
  };

  const deleteParam = (typeIndex, paramIndex) => {
    setReportTypes((prev) =>
      prev.map((type, idx) =>
        idx === typeIndex
          ? { ...type, params: type.params.filter((_, pIdx) => pIdx !== paramIndex) }
          : type
      )
    );
  };

  const updateAssetCategory = (index, patch) => {
    setAssetCardConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((category, idx) => (idx === index ? { ...category, ...patch } : category)),
    }));
  };

  const updateAssetCategoryTypes = (index, value) => {
    updateAssetCategory(index, { assetTypes: normalizeStringList(String(value || "").split(",")) });
  };

  const addAssetCategory = () => {
    setAssetCardConfig((prev) => ({ ...prev, categories: [...prev.categories, createBlankAssetCategory()] }));
  };

  const deleteAssetCategory = (index) => {
    setAssetCardConfig((prev) => ({ ...prev, categories: prev.categories.filter((_, idx) => idx !== index) }));
  };

  const updateAssetDeviceDetailsMaxItems = (value) => {
    const nextValue = Number(value);
    setAssetCardConfig((prev) => ({
      ...prev,
      deviceDetailsMaxItems: Number.isFinite(nextValue)
        ? Math.min(8, Math.max(1, Math.trunc(nextValue)))
        : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems,
    }));
  };

  const toggleAssetDeviceDetailsField = (fieldValue) => {
    setAssetCardConfig((prev) => {
      const current = new Set(prev.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);
      if (current.has(fieldValue)) {
        current.delete(fieldValue);
      } else {
        current.add(fieldValue);
      }
      const nextFields = Array.from(current);
      return {
        ...prev,
        deviceDetailsFields: nextFields.length ? nextFields : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields,
      };
    });
  };

  const excludedPermissionIdSet = useMemo(
    () => new Set(newSubscriberPermissionExclusions.excludedPermissionIds || []),
    [newSubscriberPermissionExclusions]
  );

  const filteredPermissions = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    if (!term) return permissions;
    return permissions.filter((permission) =>
      [permission.name, permission.ids, permission.category, permission.subcategory]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(term))
    );
  }, [permissions, permissionSearch]);

  const toggleExcludedPermission = (permissionId) => {
    const id = String(permissionId || "").trim();
    if (!id) return;
    setNewSubscriberPermissionExclusions((prev) => {
      const current = new Set(prev.excludedPermissionIds || []);
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return { excludedPermissionIds: Array.from(current).sort((a, b) => a.localeCompare(b)) };
    });
  };

  const clearExcludedPermissions = () => {
    setNewSubscriberPermissionExclusions({ excludedPermissionIds: [] });
  };

  const updateVoiceConfig = (patch) => {
    setAiVoiceCommands((prev) => normalizeVoiceCommandConfig({ ...prev, ...patch }));
  };

  const updateVoiceBasicAction = (index, patch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      basicActions: prev.basicActions.map((action, actionIndex) =>
        actionIndex === index ? { ...action, ...patch } : action
      ),
    }));
  };

  const updateVoiceBasicMedia = (index, mediaPatch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      basicActions: prev.basicActions.map((action, actionIndex) =>
        actionIndex === index
          ? {
              ...action,
              action: {
                type: "media",
                media: { ...(action.action?.media || {}), ...mediaPatch },
              },
            }
          : action
      ),
    }));
  };

  const addVoiceBasicAction = () => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      basicActions: [...prev.basicActions, createBlankVoiceAction()],
    }));
  };

  const deleteVoiceBasicAction = (index) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      basicActions: prev.basicActions.filter((_, actionIndex) => actionIndex !== index),
    }));
  };

  const updateVoicePage = (index, patch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        pages: prev.advanced.pages.map((page, pageIndex) => (pageIndex === index ? { ...page, ...patch } : page)),
      },
    }));
  };

  const updateVoiceEntity = (index, patch) => {
    setAiVoiceCommands((prev) => ({
      ...prev,
      advanced: {
        ...prev.advanced,
        entities: prev.advanced.entities.map((entity, entityIndex) =>
          entityIndex === index ? { ...entity, ...patch } : entity
        ),
      },
    }));
  };

  const updateWeatherCron = (patch) => {
    setWeatherCron((prev) => normalizeWeatherCronConfig({ ...prev, ...patch }));
  };

  const updateWeatherProvider = (providerId, patch) => {
    setWeatherCron((prev) =>
      normalizeWeatherCronConfig({
        ...prev,
        providers: prev.providers.map((provider) =>
          provider.id === providerId ? { ...provider, ...patch } : provider
        ),
      })
    );
  };

  const updateWeatherThreshold = (key, value) => {
    setWeatherCron((prev) =>
      normalizeWeatherCronConfig({
        ...prev,
        thresholds: { ...prev.thresholds, [key]: value },
      })
    );
  };

  const updateDashboardWidgetFontSize = (type, key, value) => {
    const parsed = Number(value);
    setDashboardWidgetFontSizes((prev) => ({
        ...prev,
        widgets: {
          ...(prev.widgets || {}),
          [type]: {
            ...(prev.widgets?.[type] || {}),
            [key]: Number.isFinite(parsed) ? parsed : value,
          },
        },
      }));
  };


  if (loading) return <Spinner />;

  if (!isSuper) {
    return (
      <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
        <div className="system-config-shell">
          <div className="system-config-empty">{t.noPermission}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration system-config-page" dir={isRtl ? "rtl" : "ltr"}>
      <div className="system-config-shell">
        <header className="system-config-header">
          <div className="system-config-title-mark">
            <SettingsSuggestIcon />
          </div>
          <div>
            <p className="system-config-eyebrow">{t.superControls}</p>
            <h2>{t.title}</h2>
            <p>{t.description}</p>
          </div>
        </header>

        <section className="system-config-status">
          <div>
            <span>{t.activeHealthReport}</span>
            <strong>{currentModeLabel}</strong>
          </div>
          <CheckCircleIcon />
        </section>

        <div className="system-config-tabs" role="tablist" aria-label={t.title}>
          <button type="button" className={activeTab === "reports" ? "active" : ""} onClick={() => setActiveTab("reports")}>
            Reports
          </button>
          <button type="button" className={activeTab === "assetCards" ? "active" : ""} onClick={() => setActiveTab("assetCards")}>
            Asset Cards
          </button>
          <button type="button" className={activeTab === "dashboardWidgets" ? "active" : ""} onClick={() => setActiveTab("dashboardWidgets")}>
            Dashboard Widgets
          </button>
          <button type="button" className={activeTab === "voiceCommands" ? "active" : ""} onClick={() => setActiveTab("voiceCommands")}>
            Voice Commands
          </button>
          <button type="button" className={activeTab === "weatherCron" ? "active" : ""} onClick={() => setActiveTab("weatherCron")}>
            Weather Cron
          </button>
          <button type="button" className={activeTab === "subscriberPermissions" ? "active" : ""} onClick={() => setActiveTab("subscriberPermissions")}>
            Subscriber Permissions
          </button>
        </div>

        {activeTab === "reports" ? (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.healthTitle}</h3>
              <p>{t.healthDescription}</p>
            </div>
          </div>

          <div className="system-config-options" role="radiogroup" aria-label={t.healthTitle}>
            {Object.entries(downloadModes).map(([key, option]) => {
              const selected = mode === key;
              return (
                <button
                  type="button"
                  key={key}
                  className={`system-config-option ${selected ? "selected" : ""}`}
                  onClick={() => setMode(key)}
                  role="radio"
                  aria-checked={selected}
                >
                  <span className="system-config-option-icon">{option.icon}</span>
                  <span>
                    <strong>{option.title}</strong>
                    <small>{option.description}</small>
                  </span>
                  <span className="system-config-radio" />
                </button>
              );
            })}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.aiAvailabilityTitle}</h3>
              <p>{t.aiAvailabilityDescription}</p>
            </div>
          </div>

          <div className="system-config-switch-grid">
            {["devices", "dashboards", "templates"].map((scope) => (
              <label className="system-config-switch-card" key={scope}>
                <span>
                  <strong>{t.aiAvailabilityScopes?.[scope] || scope}</strong>
                  <small>{t.aiAvailabilityScopeDescriptions?.[scope] || ""}</small>
                </span>
                <input
                  type="checkbox"
                  checked={aiReportAvailability[scope] !== false}
                  onChange={(event) => updateAiReportAvailability(scope, event.target.checked)}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.aiTypesTitle}</h3>
              <p>{t.aiTypesDescription}</p>
            </div>
            <button type="button" className="system-config-add-btn" onClick={addType}>
              <AddIcon /> {t.addType}
            </button>
          </div>

          <div className="system-config-report-layout">
            <div className="system-config-type-list">
              {reportTypes.length === 0 ? (
                <div className="system-config-type-empty">{t.noTypes}</div>
              ) : reportTypes.map((type, index) => (
                <button
                  type="button"
                  key={`${type.value}-${index}`}
                  className={`system-config-type-row ${selectedTypeIndex === index ? "selected" : ""}`}
                  onClick={() => setSelectedTypeIndex(index)}
                >
                  <strong>{type.label || t.untitledType}</strong>
                  <small>{type.value || t.missingKey}</small>
                </button>
              ))}
            </div>

            {selectedType ? (
              <div className="system-config-type-editor">
                <div className="system-config-editor-grid">
                  <label>
                    <span>{t.typeKey}</span>
                    <input value={selectedType.value} onChange={(e) => updateType(selectedTypeIndex, { value: e.target.value })} placeholder="power-meter" />
                  </label>
                  <label>
                    <span>{t.displayName}</span>
                    <input value={selectedType.label} onChange={(e) => updateType(selectedTypeIndex, { label: e.target.value })} placeholder="Power Meter" />
                  </label>
                  <label className="system-config-wide">
                    <span>{t.reportApi}</span>
                    <input value={selectedType.apiUrl} onChange={(e) => updateType(selectedTypeIndex, { apiUrl: e.target.value })} placeholder="/pyv1/reports/power-meter/upload-preview" />
                  </label>
                  <label>
                    <span>{t.pdfMode}</span>
                    <select value={selectedType.pdfMode || "ai_pdf"} onChange={(e) => updateType(selectedTypeIndex, { pdfMode: e.target.value })}>
                      {AI_REPORT_PDF_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>{t.pdfModes?.[mode.value] || mode.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="system-config-param-head">
                  <h4>{t.parameters}</h4>
                  <button type="button" className="system-config-add-btn secondary" onClick={() => addParam(selectedTypeIndex)}>
                    <AddIcon /> {t.addParam}
                  </button>
                </div>

                <div className="system-config-param-list">
                  {selectedType.params.map((param, paramIndex) => (
                    <div className="system-config-param-row" key={`${param.key}-${paramIndex}`}>
                      <input value={param.key} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { key: e.target.value })} placeholder={t.paramKey} />
                      <input value={param.label} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { label: e.target.value })} placeholder={t.paramLabel} />
                      <select value={param.inputType || "text"} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { inputType: e.target.value })}>
                        {AI_REPORT_PARAM_INPUT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{t.paramTypes?.[type.value] || type.label}</option>
                        ))}
                      </select>
                      <input value={param.placeholder} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { placeholder: e.target.value })} placeholder={t.paramPlaceholder} />
                      <input value={param.defaultValue || ""} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { defaultValue: e.target.value })} placeholder="Default value" />
                      <input value={serializeOptions(param.options)} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { options: normalizeAiReportParamOptions(e.target.value) })} placeholder={t.paramOptions} />
                      <label className={`system-config-check ${param.required !== false ? "is-required" : "is-optional"}`}>
                        <input type="checkbox" checked={param.required !== false} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { required: e.target.checked })} />
                        {param.required !== false ? t.required : t.optional || "Optional"}
                      </label>
                      <label className={`system-config-check ${param.system === true ? "is-required" : "is-optional"}`}>
                        <input type="checkbox" checked={param.system === true} onChange={(e) => updateParam(selectedTypeIndex, paramIndex, { system: e.target.checked })} />
                        System
                      </label>
                      <button type="button" className="system-config-icon-btn" onClick={() => deleteParam(selectedTypeIndex, paramIndex)} aria-label={t.deleteParam}>
                        <DeleteOutlineIcon />
                      </button>
                    </div>
                  ))}
                  {selectedType.params.length === 0 ? <div className="system-config-type-empty">{t.noParams}</div> : null}
                </div>

                <div className="system-config-danger-row">
                  <button type="button" className="system-config-delete-btn" onClick={() => deleteType(selectedTypeIndex)}>
                    <DeleteOutlineIcon /> {t.deleteType}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>
        </>
        ) : activeTab === "assetCards" ? (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Asset card configurations</h3>
              <p>Control categories, category-specific asset types, and the device details asset summary.</p>
            </div>
            <button type="button" className="system-config-add-btn" onClick={addAssetCategory}>
              <AddIcon /> Add category
            </button>
          </div>

          <div className="system-config-asset-summary-card">
            <div className="system-config-number-field">
              <label htmlFor="asset-device-details-max">Device details asset items</label>
              <div className="system-config-number-control">
                <button
                  type="button"
                  onClick={() => updateAssetDeviceDetailsMaxItems((assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems) - 1)}
                >
                  -
                </button>
                <input
                  id="asset-device-details-max"
                  type="number"
                  min="1"
                  max="8"
                  step="1"
                  value={assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems}
                  onChange={(event) => updateAssetDeviceDetailsMaxItems(event.target.value)}
                />
                <button
                  type="button"
                  onClick={() => updateAssetDeviceDetailsMaxItems((assetCardConfig.deviceDetailsMaxItems ?? DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems) + 1)}
                >
                  +
                </button>
              </div>
              <small>Maximum related asset cards shown in the Device Details header.</small>
            </div>

            <div className="system-config-field-picker">
              <div>
                <strong>Device details fields</strong>
                <small>Choose the asset-card fields displayed inside each compact asset pill.</small>
              </div>
              <div className="system-config-field-grid">
                {ASSET_CARD_DEVICE_DETAILS_FIELDS.map((field) => {
                  const checked = (assetCardConfig.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields).includes(field.value);
                  return (
                    <label className={`system-config-field-chip ${checked ? 'selected' : ''}`} key={field.value}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAssetDeviceDetailsField(field.value)}
                      />
                      <span>{field.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="system-config-asset-list">
            {assetCardConfig.categories.map((category, index) => (
              <div className="system-config-asset-row" key={`${category.value}-${index}`}>
                <input
                  value={category.value}
                  onChange={(event) => updateAssetCategory(index, { value: event.target.value, label: event.target.value })}
                  placeholder="Category"
                />
                <input
                  value={(category.assetTypes || []).join(", ")}
                  onChange={(event) => updateAssetCategoryTypes(index, event.target.value)}
                  placeholder="Asset types: type 1, type 2"
                />
                <button type="button" className="system-config-icon-btn" onClick={() => deleteAssetCategory(index)} aria-label="Delete category">
                  <DeleteOutlineIcon />
                </button>
              </div>
            ))}
          </div>
        </section>

        </>
        ) : activeTab === "dashboardWidgets" ? (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Dashboard widget font defaults</h3>
              <p>Set the default label and value font sizes used when users add new web dashboard widgets.</p>
            </div>
          </div>

          <div className="system-config-widget-font-layout">
            <div className="system-config-widget-font-grid">
              {DASHBOARD_WIDGET_FONT_SIZE_FIELDS.map((field) => {
                const sizes = dashboardWidgetFontSizes.widgets?.[field.type] || field;
                const isSelected = selectedDashboardWidgetField?.type === field.type;
                return (
                  <div
                    className={`system-config-widget-font-row ${isSelected ? "selected" : ""}`}
                    key={field.type}
                    onClick={() => setSelectedDashboardWidgetType(field.type)}
                  >
                    <div className="system-config-widget-font-title">
                      <DashboardCustomizeIcon />
                      <span>
                        <strong>{field.label}</strong>
                        <small>{field.type}</small>
                      </span>
                    </div>
                    <div className="system-config-widget-font-fields">
                      {(field.fields || []).map((fontField) => {
                        const isDashField = fontField.key === "dash";
                        return (
                          <label key={`${field.type}-${fontField.key}`}>
                            <span>{fontField.label}</span>
                            {isDashField ? (
                              <select
                                value={sizes[fontField.key] ?? fontField.value}
                                onFocus={() => setSelectedDashboardWidgetType(field.type)}
                                onChange={(event) => updateDashboardWidgetFontSize(field.type, fontField.key, event.target.value)}
                              >
                                <option value="none">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                              </select>
                            ) : (
                              <input
                                type="number"
                                min={fontField.key === "strokeWidth" ? "1" : "0"}
                                max={fontField.key === "strokeWidth" ? "18" : fontField.key === "bend" ? "100" : "160"}
                                step="1"
                                value={sizes[fontField.key] ?? fontField.value}
                                onFocus={() => setSelectedDashboardWidgetType(field.type)}
                                onChange={(event) => updateDashboardWidgetFontSize(field.type, fontField.key, event.target.value)}
                              />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="system-config-widget-preview" aria-label={`${selectedDashboardWidgetTitle} preview`}>
              <div className="system-config-widget-preview-headline">
                <span>{selectedDashboardWidgetTitle}</span>
                <small>{selectedDashboardWidgetField?.type}</small>
              </div>
              <div className="system-config-widget-preview-stage">
                <Widget
                  id={`system-config-preview-${selectedDashboardWidgetField?.type}`}
                  type={selectedDashboardWidgetField?.type}
                  title={selectedDashboardWidgetTitle}
                  transparentBackground={false}
                  dataId={WIDGET_PREVIEW_DATASTREAMS[0].id}
                  dataPin={WIDGET_PREVIEW_DATASTREAMS[0].pin}
                  decimals={0}
                  position="relative"
                  width={selectedDashboardWidgetSize.width}
                  height={selectedDashboardWidgetSize.height}
                  resize={false}
                  resizeWidget={() => {}}
                  draggable={false}
                  extraProps={selectedDashboardWidgetProps}
                  datastreams={WIDGET_PREVIEW_DATASTREAMS}
                  preferDatastreamDefaultValue
                />
              </div>
            </aside>
          </div>
        </section>
        </>
        ) : activeTab === "voiceCommands" ? (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Web Speech voice commands</h3>
              <p>Choose between basic wake-word media popups and advanced server-validated navigation commands.</p>
            </div>
          </div>

          <div className="system-config-voice-hero">
            <label className="system-config-switch-card">
              <span>
                <strong>Enable voice commands</strong>
                <small>Users with AI voice command permission can turn browser listening on or off from their profile menu.</small>
              </span>
              <input
                type="checkbox"
                checked={aiVoiceCommands.enabled !== false}
                onChange={(event) => updateVoiceConfig({ enabled: event.target.checked })}
              />
            </label>

            <div className="system-config-options compact" role="radiogroup" aria-label="Voice command mode">
              {[
                { key: "basic", icon: <FolderOpenIcon />, title: "Basic Web Speech API", description: "Wake word or sentence opens a selected server file, image, or video popup." },
                { key: "advanced", icon: <NavigationIcon />, title: "Advanced commands", description: "Wake word plus command can navigate pages and open permitted entity details." },
              ].map((option) => {
                const selected = aiVoiceCommands.mode === option.key;
                return (
                  <button
                    type="button"
                    key={option.key}
                    className={`system-config-option ${selected ? "selected" : ""}`}
                    onClick={() => updateVoiceConfig({ mode: option.key })}
                    role="radio"
                    aria-checked={selected}
                  >
                    <span className="system-config-option-icon">{option.icon}</span>
                    <span>
                      <strong>{option.title}</strong>
                      <small>{option.description}</small>
                    </span>
                    <span className="system-config-radio" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="system-config-editor-grid voice">
            <label>
              <span>Wake words</span>
              <input
                value={(aiVoiceCommands.wakeWords || []).join(", ")}
                onChange={(event) => updateVoiceConfig({ wakeWords: normalizeStringList(event.target.value.split(",")) })}
                placeholder="sama, سما"
              />
            </label>
            <label>
              <span>Speech languages</span>
              <input
                value={(aiVoiceCommands.languages || []).join(", ")}
                onChange={(event) => updateVoiceConfig({ languages: normalizeStringList(event.target.value.split(",")) })}
                placeholder="en-US, ar-SA"
              />
            </label>
            <label className="system-config-wide">
              <span>No permission response</span>
              <input
                value={aiVoiceCommands.deniedMessage || ""}
                onChange={(event) => updateVoiceConfig({ deniedMessage: event.target.value })}
              />
            </label>
            <label className="system-config-wide">
              <span>Unknown request response</span>
              <input
                value={aiVoiceCommands.unknownMessage || ""}
                onChange={(event) => updateVoiceConfig({ unknownMessage: event.target.value })}
              />
            </label>
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Basic media actions</h3>
              <p>Add one or many spoken phrases and choose the media shown in the popup from server files.</p>
            </div>
            <button type="button" className="system-config-add-btn" onClick={addVoiceBasicAction}>
              <AddIcon /> Add action
            </button>
          </div>

          <div className="system-config-voice-actions">
            {(aiVoiceCommands.basicActions || []).map((action, index) => (
              <div className="system-config-voice-row" key={action.id || index}>
                <div className="system-config-voice-row-title">
                  <MicIcon />
                  <strong>{action.label || "Voice media action"}</strong>
                  <button type="button" className="system-config-icon-btn" onClick={() => deleteVoiceBasicAction(index)} aria-label="Delete voice action">
                    <DeleteOutlineIcon />
                  </button>
                </div>
                <div className="system-config-editor-grid voice">
                  <label>
                    <span>Action label</span>
                    <input value={action.label || ""} onChange={(event) => updateVoiceBasicAction(index, { label: event.target.value })} />
                  </label>
                  <label>
                    <span>Words / sentences</span>
                    <input
                      value={(action.phrases || []).join(", ")}
                      onChange={(event) => updateVoiceBasicAction(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                      placeholder="sama, sama show intro"
                    />
                  </label>
                  <label className="system-config-wide">
                    <span>Server media</span>
                    <select
                      value={action.action?.media?.path || ""}
                      onChange={(event) => {
                        const selected = voiceMediaLibrary.find((item) => item.path === event.target.value);
                        updateVoiceBasicMedia(index, {
                          path: event.target.value,
                          title: selected?.title || action.action?.media?.title || "",
                          mediaType: selected?.mediaType || action.action?.media?.mediaType || "file",
                        });
                      }}
                    >
                      <option value="">Choose a server file</option>
                      {voiceMediaLibrary.map((item) => (
                        <option key={item.path} value={item.path}>
                          {item.mediaType.toUpperCase()} / {item.path}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>Popup title</span>
                    <input
                      value={action.action?.media?.title || ""}
                      onChange={(event) => updateVoiceBasicMedia(index, { title: event.target.value })}
                    />
                  </label>
                  <label>
                    <span>Media type</span>
                    <select
                      value={action.action?.media?.mediaType || "image"}
                      onChange={(event) => updateVoiceBasicMedia(index, { mediaType: event.target.value })}
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="file">File</option>
                    </select>
                  </label>
                </div>
              </div>
            ))}
            {aiVoiceCommands.basicActions?.length ? null : (
              <div className="system-config-type-empty">No basic voice actions yet.</div>
            )}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Advanced navigation</h3>
              <p>These commands are applied to all users, but every command is checked against permissions before navigation.</p>
            </div>
          </div>

          <div className="system-config-voice-split">
            <div>
              <h4>Main pages</h4>
              {(aiVoiceCommands.advanced?.pages || []).map((page, index) => (
                <div className="system-config-voice-mini-row" key={page.id || index}>
                  <strong>{page.label}</strong>
                  <small>{page.path}</small>
                  <input
                    value={(page.phrases || []).join(", ")}
                    onChange={(event) => updateVoicePage(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                    placeholder="open devices page, go to devices"
                  />
                </div>
              ))}
            </div>
            <div>
              <h4>Entity details</h4>
              {(aiVoiceCommands.advanced?.entities || []).map((entity, index) => (
                <div className="system-config-voice-mini-row" key={entity.id || index}>
                  <strong>{entity.label}</strong>
                  <small>{entity.pathPrefix}/:id</small>
                  <input
                    value={(entity.phrases || []).join(", ")}
                    onChange={(event) => updateVoiceEntity(index, { phrases: normalizeStringList(event.target.value.split(",")) })}
                    placeholder="open device details, open device"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
        </>
        ) : activeTab === "weatherCron" ? (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>UAE weather and dust cron</h3>
              <p>Control cron state, provider selection, schedule interval, and dust classification thresholds.</p>
            </div>
          </div>

          <div className="system-config-weather-hero">
            <label className="system-config-switch-card">
              <span>
                <strong>Enable weather cron</strong>
                <small>When disabled, the cron worker skips UAE weather and dust sync.</small>
              </span>
              <input
                type="checkbox"
                checked={weatherCron.enabled !== false}
                onChange={(event) => updateWeatherCron({ enabled: event.target.checked })}
              />
            </label>

            <div className="system-config-editor-grid weather">
              <label>
                <span>Interval minutes</span>
                <input
                  type="number"
                  min="15"
                  step="1"
                  value={weatherCron.intervalMinutes}
                  onChange={(event) => updateWeatherCron({ intervalMinutes: event.target.value })}
                />
              </label>
              <label>
                <span>Timeout ms</span>
                <input
                  type="number"
                  min="1000"
                  step="500"
                  value={weatherCron.requestTimeoutMs}
                  onChange={(event) => updateWeatherCron({ requestTimeoutMs: event.target.value })}
                />
              </label>
              <label className="system-config-wide">
                <span>Timezone</span>
                <input
                  value={weatherCron.timezone}
                  onChange={(event) => updateWeatherCron({ timezone: event.target.value })}
                  placeholder="Asia/Dubai"
                />
              </label>
            </div>
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Weather providers</h3>
              <p>The cron stores every enabled provider response in the weather status audit payload.</p>
            </div>
          </div>

          <div className="system-config-provider-grid">
            {weatherCron.providers.map((provider) => (
              <label className={`system-config-provider-card ${provider.enabled !== false ? "enabled" : ""}`} key={provider.id}>
                <span className="system-config-provider-icon"><CloudQueueIcon /></span>
                <span>
                  <strong>{WEATHER_PROVIDER_LABELS[provider.id] || provider.id}</strong>
                  <small>{provider.role === "weather" ? "General weather verification" : "Air quality / dust source"}</small>
                  {provider.apiKeyEnv ? <em>API key env: {provider.apiKeyEnv}</em> : null}
                </span>
                <input
                  type="checkbox"
                  checked={provider.enabled !== false}
                  onChange={(event) => updateWeatherProvider(provider.id, { enabled: event.target.checked })}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>Dust thresholds</h3>
              <p>These values decide whether saved readings become clear, hazy, dusty, heavy dust, or sandstorm.</p>
            </div>
          </div>

          <div className="system-config-threshold-grid">
            {WEATHER_THRESHOLD_FIELDS.map((field) => (
              <label key={field.key}>
                <span>{field.label}</span>
                <input
                  type="number"
                  min="0"
                  step={field.key === "hazyAerosolOpticalDepth" ? "0.01" : "1"}
                  value={weatherCron.thresholds[field.key]}
                  onChange={(event) => updateWeatherThreshold(field.key, event.target.value)}
                />
                <small>{field.unit}</small>
              </label>
            ))}
          </div>
        </section>
        </>
        ) : (
        <>
        <section className="system-config-panel">
          <div className="system-config-panel-head">
            <div>
              <h3>{t.newSubscriberPermissionsTitle || 'New subscriber default permissions'}</h3>
              <p>{t.newSubscriberPermissionsDescription || 'New subscriber Owner roles receive all permissions except the permissions selected below.'}</p>
            </div>
            <button type="button" className="system-config-add-btn secondary" onClick={clearExcludedPermissions} disabled={!newSubscriberPermissionExclusions.excludedPermissionIds.length}>
              {t.clearExcludedPermissions || 'Clear exclusions'}
            </button>
          </div>

          <div className="system-config-permission-summary">
            <strong>{newSubscriberPermissionExclusions.excludedPermissionIds.length}</strong>
            <span>{t.excludedPermissionsCount || 'permissions excluded from new subscribers'}</span>
          </div>

          <input
            className="system-config-permission-search"
            value={permissionSearch}
            onChange={(event) => setPermissionSearch(event.target.value)}
            placeholder={t.searchPermissions || 'Search permissions'}
          />

          <div className="system-config-permission-list">
            {filteredPermissions.map((permission) => {
              const permissionKey = String(permission.ids || permission.id);
              const checked = excludedPermissionIdSet.has(permissionKey) || excludedPermissionIdSet.has(String(permission.id));
              return (
                <label className={`system-config-permission-row ${checked ? 'excluded' : ''}`} key={permission.id}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleExcludedPermission(permissionKey)}
                  />
                  <span>
                    <strong>{permission.name || permission.ids}</strong>
                    <small>{permission.ids} · {[permission.category, permission.subcategory].filter(Boolean).join(' / ')}</small>
                  </span>
                  <em>{checked ? (t.excluded || 'Excluded') : (t.willBeAdded || 'Will be added')}</em>
                </label>
              );
            })}
            {filteredPermissions.length === 0 ? <div className="system-config-type-empty">{t.noPermissionsFound || 'No permissions found.'}</div> : null}
          </div>
        </section>
        </>
        )}

        <div className="system-config-actions">
          {message ? <span className="system-config-message">{message}</span> : <span />}
          <button type="button" className="devices-primary-btn" disabled={!isDirty || saving} onClick={save}>
            {saving ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemConfigurations;
